// src/pages/Users/index.js
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UsersTable from '../../components/Users/UsersTable';
import { fetchUsers, fetchIdentityVerification } from '../../redux/slices/userSlice';

const UsersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusTab = (searchParams.get('status') || 'all').toLowerCase();
  // 1) give users a default empty array
  const { users = [], loading, error, loadingVerification } = useSelector((state) => state.users || { users: [] });

  // Initial filter parameters (with a high limit to get all records)
  const [filterParams, setFilterParams] = useState({
    limit: 100,
    currentPage: 1,
    byKeyword: "",
  });

  // Local pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // Display 10 users per page

  // Fetch users whenever filterParams change
  useEffect(() => {
    dispatch(fetchUsers(filterParams));
  }, [dispatch, filterParams]);

  // Reset to page 1 whenever the topbar status tab changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [statusTab]);

  // Local filter: keyword search + the topbar status tab (?status=...).
  const filteredUsers = useMemo(() => {
    let list = users;
    if (filterParams.byKeyword) {
      const keyword = filterParams.byKeyword.toLowerCase().trim();
      list = list.filter((user) =>
        user.name.toLowerCase().includes(keyword) ||
        (user.email && user.email.toLowerCase().includes(keyword)) ||
        (user.phone && user.phone.includes(keyword))
      );
    }
    if (statusTab && statusTab !== 'all') {
      list = list.filter((u) => (u.accountStatus || '').toLowerCase() === statusTab);
    }
    return list;
  }, [users, filterParams.byKeyword, statusTab]);

  // 2) guard against filteredUsers being null
  const safeUsers = useMemo(() => filteredUsers || [], [filteredUsers]);

  // Calculate total pages and get the current page's slice of users
  const totalPages = Math.ceil(safeUsers.length / pageSize);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return safeUsers.slice(startIndex, startIndex + pageSize);
  }, [safeUsers, currentPage, pageSize]);

  // Handler passed to the top bar to update filter parameters
  const handleFilter = (filterObj) => {
    setFilterParams({ ...filterParams, byKeyword: filterObj.byKeyword, currentPage: 1 });
    setCurrentPage(1);
  };

  // Handler for verification request using Redux
  const handleVerificationRequest = async () => {
    try {
      await dispatch(fetchIdentityVerification()).unwrap();
      // Removed alert - data loads directly without confirmation
    } catch (error) {
      console.error('Error fetching verification data:', error);
      // Only show error alerts, not success alerts
      alert('Failed to load verification data. Please try again.');
    }
  };

  // NEW: Handler for when user clicks on pending verification status
  const handleVerificationClick = (user) => {
    // Navigate to user details with verification tab and pass the user data
    navigate(`/users/${user._id}?tab=verification`, {
      state: { userData: user }
    });
  };

  return (
    <div className="p-4">
      <div className="mt-4">
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : (
          <UsersTable 
            users={paginatedUsers}
            currentPage={currentPage}
            totalPages={totalPages}
            total={safeUsers.length}
            onPageChange={setCurrentPage}
            loading={loading || loadingVerification}
            onVerificationClick={handleVerificationClick}
            onFilter={handleFilter}
            onVerificationRequest={handleVerificationRequest}
          />
        )}
      </div>
    </div>
  );
};

export default UsersPage;
